import React from "react";
import {
  Box,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorMode,
} from "@chakra-ui/react";

/**
 * Render a Chakra Table from a `columns` config and `data` rows.
 *
 * `columns` shape: [{ key, header, render?, thProps?, tdProps? }]
 *  - `header` is the heading content; if omitted, `key` is used.
 *  - `render(row, index)` returns the cell node; defaults to `row[key]`.
 *
 * Cell- or row-level concerns (sortable headers, row click handlers, custom
 * cell formatting) are passed through `header` / `render` so callers retain
 * full control without needing an escape hatch.
 */
const DataTable = ({
  columns = [],
  data = [],
  size = "sm",
  variant = "simple",
  rounded = "lg",
  stickyHeader = true,
  emptyMessage = "No data to display",
  rowKey,
  containerProps,
  tableProps,
}) => {
  const { colorMode } = useColorMode();
  const headerBg = colorMode === "light" ? "white" : "gray.700";
  const borderColor = colorMode === "light" ? "gray.100" : "gray.800";

  if (!data || data.length === 0) {
    return (
      <Box py={4} textAlign="center">
        <Text fontSize="sm" color="gray.500">
          {emptyMessage}
        </Text>
      </Box>
    );
  }

  const getRowKey = (row, index) => {
    if (typeof rowKey === "function") return rowKey(row, index);
    if (typeof rowKey === "string" && row[rowKey] != null) return row[rowKey];
    return index;
  };

  return (
    <TableContainer overflowY="auto" overflowX="auto" rounded={rounded} {...containerProps}>
      <Table size={size} variant={variant} borderColor={borderColor} {...tableProps}>
        <Thead
          {...(stickyHeader
            ? { position: "sticky", top: 0, bgColor: headerBg, zIndex: 2 }
            : {})}
        >
          <Tr>
            {columns.map((col) => (
              <Th key={col.key} {...col.thProps}>
                {col.header ?? col.key}
              </Th>
            ))}
          </Tr>
        </Thead>
        <Tbody>
          {data.map((row, index) => (
            <Tr key={getRowKey(row, index)}>
              {columns.map((col) => (
                <Td key={col.key} {...col.tdProps}>
                  {col.render ? col.render(row, index) : row[col.key]}
                </Td>
              ))}
            </Tr>
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  );
};

export default DataTable;
